import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import ProcessLoader from "../ProcessLoader";
import { proxy } from "../../process/api";
import DropDownControl from "../DropDownControl";
import {getUserType} from "./../../utils";


const REPORT_TITLE = "MANAGERS CHEMIST AUDIT REPORT";
const MARKETING_YEARS = ["2024-25", "2025-26", "2026-27"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];

const monthToShort = {
  January: "Jan",
  February: "Feb",
  March: "Mar",
  April: "Apr",
  May: "May",
  June: "Jun",
  July: "Jul",
  August: "Aug",
  September: "Sep",
  October: "Oct",
  November: "Nov",
  December: "Dec"
};

const shortToMonth = Object.fromEntries(
  Object.entries(monthToShort).map(([k, v]) => [v, k])
);

const emptyCompetitorRow = () => ({
  competitor_top_selling_brand: "",
  top_prescribing_doctor_1_name: "",
  top_prescribing_doctor_1_patients_per_day: "",
  top_prescribing_doctor_2_name: "",
  top_prescribing_doctor_2_patients_per_day: "",
  top_prescribing_doctor_3_name: "",
  top_prescribing_doctor_3_patients_per_day: ""
});

const emptyBrandGroup = () => ({
  aristoBrand: "",
  potential_per_month_strips: "",
  our_sales_per_month_strips: "",
  rows: [emptyCompetitorRow()]
});

const createSheet = (index) => ({
  id: Date.now() + index,
  auditInnerSheetId: null,
  psr_code: 0,
  division: "",
  branch: "",
  psr_id: "",
  psr_name: "",
  hq: "",
  chemist_name: "",
  brandGroups: [emptyBrandGroup()]
});

const createSheetForInnerId = (index, innerSheetId) => ({
  id: Date.now() + index,
  auditInnerSheetId: innerSheetId || null,
  psr_code: 0,
  division: "",
  branch: "",
  psr_id: "",
  psr_name: "",
  hq: "",
  chemist_name: "",
  brandGroups: [emptyBrandGroup()]
});

const getMonthCode = (month, marketingYear) => {
  if (!month || !marketingYear) return "";

const [startYear, endYear] = String(marketingYear ?? "");
  const monthIndex = MONTHS.indexOf(month);
  if (monthIndex === -1) return "";

  const usesEndYear = monthIndex <= 2;
  const yearSuffix = (usesEndYear ? endYear : startYear).slice(-2);
  return `${monthToShort[month]}-${yearSuffix}`;
};

const getNaturalMonthFromShortCode = (monthCode) => {
  if (!monthCode || typeof monthCode !== "string") return "";
  const shortMonth = monthCode.split("-")[0];
  return shortToMonth[shortMonth] || "";
};

const getUsableMonthStringFromMetaMonthCode = (metaMonthCode) => {
  const raw = safeString(metaMonthCode).trim();
  if (!raw) return "";
  const normalized = raw.padStart(6, "0");
  const year = normalized.slice(0, 4);
  const monthPart = Number(normalized.slice(4, 6));
  if (!Number.isFinite(monthPart) || monthPart < 1 || monthPart > 12) return "";
  const monthName = MONTHS[monthPart - 1];
  return `${monthToShort[monthName]}-${year.slice(-2)}`;
};

const buildMarketingYear = (myear) => {
  const start = Number(myear);
  if (!Number.isFinite(start)) return "";
  return `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
};

const numberOrZero = (value) => {
  if (value === "" || value === null || typeof value === "undefined") return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const safeString = (value) => {
  if (value === null || typeof value === "undefined") return "";
  return String(value);
};

const normalizeInnerSheetId = (value) => {
  const str = safeString(value).trim();
  if (!str) return null;
  const numeric = Number(str);
  if (!Number.isFinite(numeric)) return null;
  return Math.trunc(numeric);
};

const sanitizeExcelSheetName = (name, fallbackName) => {
  const cleaned = safeString(name || fallbackName)
    .replace(/[\[\]\*\/\\\:\?]/g, " ")
    .trim();
  const safe = cleaned || fallbackName;
  return safe.slice(0, 31);
};

const mapBrandResponseToOptions = (response) => {
  const rawList = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response?.data?.data)
      ? response.data.data
      : [];

  return rawList
    .map((item) => safeString(item?.name).trim())
    .filter(Boolean)
    .map((brandName) => ({ name: brandName, value: brandName }));
};

const mapMrResponseToList = (response) => {
  const rawList = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
      ? response
      : [];

  return rawList.map((item) => ({
    value: safeString(item?.value),
    name: safeString(item?.name),
    hq: safeString(item?.hq)
  }));
};

const getNameFromLocalStorageMap = (storageKey, code) => {
  if (code === null || typeof code === "undefined" || code === "") return "";
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    const list = Array.isArray(parsed?.data) ? parsed.data : [];
    const found = list.find((item) => String(item?.value) === String(code));
    return safeString(found?.name);
  } catch (error) {
    return "";
  }
};

const mapApiSheetToState = (apiSheet, auditInnerSheetId, index) => {
  const brands = apiSheet?.["ARISTO Brand"] || {};
  const brandGroups = Object.entries(brands).map(([brandName, brandValue]) => ({
    aristoBrand: safeString(brandName),
    potential_per_month_strips: safeString(brandValue?.potential_per_month_strips),
    our_sales_per_month_strips: safeString(brandValue?.our_sales_per_month_strips),
    rows: Array.isArray(brandValue?.data) && brandValue.data.length
      ? brandValue.data.map((row) => ({
        competitor_top_selling_brand: safeString(row?.competitor_top_selling_brand),
        top_prescribing_doctor_1_name: safeString(row?.top_prescribing_doctor_1_name),
        top_prescribing_doctor_1_patients_per_day: safeString(
          row?.top_prescribing_doctor_1_patients_per_day
        ),
        top_prescribing_doctor_2_name: safeString(row?.top_prescribing_doctor_2_name),
        top_prescribing_doctor_2_patients_per_day: safeString(
          row?.top_prescribing_doctor_2_patients_per_day
        ),
        top_prescribing_doctor_3_name: safeString(row?.top_prescribing_doctor_3_name),
        top_prescribing_doctor_3_patients_per_day: safeString(
          row?.top_prescribing_doctor_3_patients_per_day
        )
      }))
      : [emptyCompetitorRow()]
  }));

  return {
    id: Date.now() + index,
    auditInnerSheetId: normalizeInnerSheetId(auditInnerSheetId),
    psr_code: 0,
    division: safeString(apiSheet?.Division),
    branch: safeString(apiSheet?.Branch),
    psr_id: safeString(apiSheet?.psr_id || apiSheet?.mr_id),
    psr_name: safeString(apiSheet?.psr_name || apiSheet?.name),
    hq: safeString(apiSheet?.hq || apiSheet?.HQ),
    chemist_name: safeString(apiSheet?.chemist_name),
    brandGroups: brandGroups.length ? brandGroups : [emptyBrandGroup()]
  };
};

const ChemistAuditEdit = () => {
  const userType = getUserType();
  const { auditreportid,createdbyManager } = useParams();

  const [showProcessLoader, setShowProcessLoader] = useState(true);
  const [header, setHeader] = useState({
    division: "",
    branch: "",
    hq: "",
    area: "",
    marketingYear: "",
    month: ""
  });
  const [auditMeta, setAuditMeta] = useState({
    auditReportTitle: "",
    auditReportId: Number(auditreportid) || null,
    auditReportStatus: "DRAFT",
    monthCode: ""
  });
  const [auditInnerSheetIds, setAuditInnerSheetIds] = useState([]);
  const [brandDropdownData, setBrandDropdownData] = useState([]);
  const [mrList, setMrList] = useState([]);
  const [abmJSON, setAbmJSON] = useState({});
  const [selectedMetaPsrCode, setSelectedMetaPsrCode] = useState("");
  const [sheets, setSheets] = useState([createSheet(1)]);
  const [activeSheetId, setActiveSheetId] = useState(sheets[0].id);
  const navigate = useNavigate();
  const monthCode = useMemo(
    () => getMonthCode(header.month, header.marketingYear),
    [header.month, header.marketingYear]
  );

  const UsableMonthString = useMemo(
    () => getUsableMonthStringFromMetaMonthCode(auditMeta.monthCode),
    [auditMeta.monthCode]
  );

  const activeSheet = useMemo(
    () => sheets.find((sheet) => sheet.id === activeSheetId) || sheets[0],
    [sheets, activeSheetId]
  );
  const commonPsrCode = useMemo(
    () => selectedMetaPsrCode || (sheets[0]?.psr_code ? String(sheets[0].psr_code) : ""),
    [selectedMetaPsrCode, sheets]
  );
  const commonPsr = useMemo(
    () => mrList.find((item) => String(item.value) === String(commonPsrCode)),
    [mrList, commonPsrCode]
  );

  const brandOptions = useMemo(() => {
    const base = Array.isArray(brandDropdownData) ? brandDropdownData : [];
    const existingNames = new Set(base.map((item) => item.name));
    const fromLoadedSheets = [];

    sheets.forEach((sheet) => {
      sheet.brandGroups.forEach((group) => {
        if (group.aristoBrand && !existingNames.has(group.aristoBrand)) {
          existingNames.add(group.aristoBrand);
          fromLoadedSheets.push({
            name: group.aristoBrand,
            value: group.aristoBrand
          });
        }
      });
    });

    return [{ name: "Select brand", value: "" }, ...base, ...fromLoadedSheets];
  }, [brandDropdownData, sheets]);

  const mrOptions = useMemo(
    () => [{ name: "Select MR", value: "" }, ...mrList],
    [mrList]
  );
  const psrCodeOptions = useMemo(
    () => [
      { name: "Select PSR Code", value: "" },
      ...mrList.map((item) => ({
        name: `${item.value} - ${item.name}`,
        value: item.value
      }))
    ],
    [mrList]
  );

  useEffect(() => {
    let isMounted = true;

    const loadMRList = async () => {
      try {
        const url =
  userType === 4
    ? "/api/mis/psrlist?createdBy="+createdbyManager
    : "/api/mis/psrlist?createdBy="+createdbyManager;


        const mrAPIresponse = await proxy(url, {}, "get");
        const mapped = mapMrResponseToList(mrAPIresponse);
        if (isMounted) {
          setMrList(mapped);
          if (mapped.length > 0) {
            setSelectedMetaPsrCode((prev) => prev || String(mapped[0].value));
          }
        }
      } catch (error) {
        console.error("Failed to load MR list", error);
        if (isMounted) {
          setMrList([]);
        }
      }
    };

    loadMRList();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadAuditData = async () => {
      setShowProcessLoader(true);
      try {
        const meta = await proxy(
          `/api/mis/abm/getauditreportmeta/${auditreportid}/${selectedMetaPsrCode}`,
          {},
          "get"
        );

        if (meta?.abmDraftId) {
          try {
            const abmResponse = await proxy(
              `/api/mis/abm/report/${meta.abmDraftId}`,
              {},
              "get"
            );
            if (isMounted) {
              setAbmJSON(abmResponse || {});
            }
          } catch (error) {
            console.error("Failed to load ABM JSON", error);
            if (isMounted) {
              setAbmJSON({});
            }
          }
        } else if (isMounted) {
          setAbmJSON({});
        }

        try {
          const brandListResponse = await proxy(
            `/api/mis/brandlist/${meta?.myear}/${meta?.divCode}`,
            {},
            "get"
          );
          const loadedBrandData = mapBrandResponseToOptions(brandListResponse);
          if (isMounted) {
            setBrandDropdownData(loadedBrandData);
          }
        } catch (error) {
          console.error("Failed to load brand list", error);
          if (isMounted) {
            setBrandDropdownData([]);
          }
        }

        const innerSheetIds = Array.isArray(meta?.auditInnerSheetIds)
          ? meta.auditInnerSheetIds
          : [];

        const sheetResults = await Promise.allSettled(
          innerSheetIds.map((innerSheetId) =>
            proxy(`/api/mis/abm/getauditsheet/${auditreportid}/${innerSheetId}`, {}, "get")
          )
        );

        const loadedSheets = innerSheetIds.length
          ? innerSheetIds.map((innerSheetId, idx) => {
            const result = sheetResults[idx];
            if (
              result?.status === "fulfilled" &&
              Array.isArray(result?.value?.sheets) &&
              result.value.sheets[0]
            ) {
              return mapApiSheetToState(result.value.sheets[0], innerSheetId, idx + 1);
            }
            return createSheetForInnerId(idx + 1, innerSheetId);
          })
          : [createSheet(1)];

        const firstValidResult = sheetResults.find(
          (result) =>
            result?.status === "fulfilled" &&
            Array.isArray(result?.value?.sheets) &&
            result.value.sheets[0]
        );
        const firstSheet =
          firstValidResult?.status === "fulfilled" ? firstValidResult.value.sheets[0] : {};

        const divisionName =
          safeString(firstSheet?.Division) ||
          getNameFromLocalStorageMap("div_data", meta?.divCode);
        const branchName =
          safeString(firstSheet?.Branch) ||
          getNameFromLocalStorageMap("depo_data", meta?.depoCode);

        const loadedHeader = {
          division: divisionName,
          branch: branchName,
          hq: safeString(firstSheet?.HQ || firstSheet?.hq || meta?.hq),
          area: safeString(firstSheet?.Area || firstSheet?.AREA),
          marketingYear: meta?.myear,
          month: getNaturalMonthFromShortCode(firstSheet?.month)
        };

        if (!isMounted) return;

        setAuditMeta({
          auditReportTitle: safeString(meta?.auditReportTitle),
          auditReportId: Number(meta?.auditReportId || auditreportid) || null,
          auditReportStatus: safeString(meta?.auditReportStatus || "DRAFT"),
          monthCode: safeString(meta?.monthCode),
          auditDraftId:meta?.abmDraftId
        });
        setAuditInnerSheetIds(innerSheetIds);
        setHeader(loadedHeader);
        setSheets(loadedSheets);
        setActiveSheetId(loadedSheets[0].id);
      } catch (error) {
        console.error("Failed to load audit report", error);
        if (isMounted) {
          alert("Unable to load audit report in edit mode.");
        }
      } finally {
        if (isMounted) {
          setShowProcessLoader(false);
        }
      }
    };

    if (auditreportid && selectedMetaPsrCode) {
      loadAuditData();
    } else {
      setShowProcessLoader(false);
    }

    return () => {
      isMounted = false;
    };
  }, [auditreportid, selectedMetaPsrCode]);

  const updateHeader = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const updateSheet = (sheetId, updates) => {
    setSheets((prev) =>
      prev.map((sheet) =>
        sheet.id === sheetId ? { ...sheet, ...updates } : sheet
      )
    );
  };

  const handlePsrCodeSelect = (selectedPsrCode) => {
    const selectedMr = mrList.find(
      (item) => String(item.value) === String(selectedPsrCode)
    );
    setSelectedMetaPsrCode(safeString(selectedPsrCode));

    setSheets((prev) =>
      prev.map((sheet) => ({
        ...sheet,
        psr_code: Number(selectedPsrCode) || 0,
        psr_id: safeString(selectedPsrCode),
        psr_name: safeString(selectedMr?.name),
        hq: safeString(selectedMr?.hq)
      }))
    );
  };

  useEffect(() => {
    if (!selectedMetaPsrCode) return;
    const selectedMr = mrList.find(
      (item) => String(item.value) === String(selectedMetaPsrCode)
    );
    if (!selectedMr) return;

    setSheets((prev) =>
      prev.map((sheet) => ({
        ...sheet,
        psr_code: Number(selectedMetaPsrCode) || 0,
        psr_id: safeString(selectedMetaPsrCode),
        psr_name: safeString(selectedMr?.name),
        hq: safeString(selectedMr?.hq)
      }))
    );
  }, [selectedMetaPsrCode, mrList]);

  const addSheet = () => {
    if (sheets.length >= 10) return;
    const newSheet = {
      ...createSheet(sheets.length + 1),
      psr_code: Number(commonPsrCode) || 0,
      psr_id: safeString(commonPsr?.value),
      psr_name: safeString(commonPsr?.name),
      hq: safeString(commonPsr?.hq)
    };
    setSheets((prev) => [...prev, newSheet]);
    setAuditInnerSheetIds((prev) => [...prev, null]);
    setActiveSheetId(newSheet.id);
  };

  const removeSheet = (sheetId) => {
    if (sheets.length === 1) return;

    const idx = sheets.findIndex((sheet) => sheet.id === sheetId);
    const nextSheets = sheets.filter((sheet) => sheet.id !== sheetId);
    const nextInnerSheetIds = auditInnerSheetIds.filter((_, index) => index !== idx);

    setSheets(nextSheets);
    setAuditInnerSheetIds(nextInnerSheetIds);

    if (activeSheetId === sheetId) {
      const fallback = nextSheets[Math.max(0, idx - 1)] || nextSheets[0];
      setActiveSheetId(fallback.id);
    }
  };

  const addBrandGroup = (sheetId) => {
    setSheets((prev) =>
      prev.map((sheet) =>
        sheet.id === sheetId
          ? { ...sheet, brandGroups: [...sheet.brandGroups, emptyBrandGroup()] }
          : sheet
      )
    );
  };

  const removeBrandGroup = (sheetId, groupIndex) => {
    setSheets((prev) =>
      prev.map((sheet) => {
        if (sheet.id !== sheetId) return sheet;
        if (sheet.brandGroups.length === 1) return sheet;

        return {
          ...sheet,
          brandGroups: sheet.brandGroups.filter((_, idx) => idx !== groupIndex)
        };
      })
    );
  };

  const updateBrandGroup = (sheetId, groupIndex, field, value) => {
    setSheets((prev) =>
      prev.map((sheet) => {
        if (sheet.id !== sheetId) return sheet;

        const nextGroups = [...sheet.brandGroups];
        nextGroups[groupIndex] = { ...nextGroups[groupIndex], [field]: value };
        return { ...sheet, brandGroups: nextGroups };
      })
    );
  };

  const addCompetitorRow = (sheetId, groupIndex) => {
    setSheets((prev) =>
      prev.map((sheet) => {
        if (sheet.id !== sheetId) return sheet;

        const nextGroups = [...sheet.brandGroups];
        nextGroups[groupIndex] = {
          ...nextGroups[groupIndex],
          rows: [...nextGroups[groupIndex].rows, emptyCompetitorRow()]
        };

        return { ...sheet, brandGroups: nextGroups };
      })
    );
  };

  const removeCompetitorRow = (sheetId, groupIndex, rowIndex) => {
    setSheets((prev) =>
      prev.map((sheet) => {
        if (sheet.id !== sheetId) return sheet;

        const nextGroups = [...sheet.brandGroups];
        const currentGroup = nextGroups[groupIndex];
        if (currentGroup.rows.length === 1) return sheet;

        nextGroups[groupIndex] = {
          ...currentGroup,
          rows: currentGroup.rows.filter((_, idx) => idx !== rowIndex)
        };

        return { ...sheet, brandGroups: nextGroups };
      })
    );
  };

  const updateCompetitorRow = (sheetId, groupIndex, rowIndex, field, value) => {
    setSheets((prev) =>
      prev.map((sheet) => {
        if (sheet.id !== sheetId) return sheet;

        const nextGroups = [...sheet.brandGroups];
        const nextRows = [...nextGroups[groupIndex].rows];
        nextRows[rowIndex] = { ...nextRows[rowIndex], [field]: value };
        nextGroups[groupIndex] = { ...nextGroups[groupIndex], rows: nextRows };

        return { ...sheet, brandGroups: nextGroups };
      })
    );
  };

  const getMissingBrandSelection = () => {
    for (let sIdx = 0; sIdx < sheets.length; sIdx += 1) {
      const groups = sheets[sIdx]?.brandGroups || [];
      for (let gIdx = 0; gIdx < groups.length; gIdx += 1) {
        if (!safeString(groups[gIdx]?.aristoBrand).trim()) {
          return { sheetIndex: sIdx, groupIndex: gIdx };
        }
      }
    }
    return null;
  };

  const buildPayload = (status) => {
    const titlePrefix = "CHEMIST AUDIT REPORT";
    const generatedTitle = `${titlePrefix} ${header.division || ""} ${monthCode || ""} ${header.hq || ""}`.trim();
    const selectedPsr = mrList.find(
      (item) => String(item.value) === String(selectedMetaPsrCode)
    );
    const selectedPsrHq = safeString(selectedPsr?.hq);

    return {
      audit_report_title: generatedTitle,
      audit_report_id: auditMeta.auditReportId || Number(auditreportid) || Date.now(),
      audit_report_status: status,
      audit_inner_sheet_ids: auditInnerSheetIds,
      sheets: sheets.map((sheet, sheetIndex) => {
        const brandObject = {};

        sheet.brandGroups.forEach((group) => {
          const brandKey = (group.aristoBrand || "").trim();
          brandObject[brandKey] = {
            potential_per_month_strips: numberOrZero(group.potential_per_month_strips),
            our_sales_per_month_strips: numberOrZero(group.our_sales_per_month_strips),
            data: group.rows.map((row) => ({
              competitor_top_selling_brand: row.competitor_top_selling_brand,
              top_prescribing_doctor_1_name: row.top_prescribing_doctor_1_name,
              top_prescribing_doctor_1_patients_per_day: numberOrZero(row.top_prescribing_doctor_1_patients_per_day),
              top_prescribing_doctor_2_name: row.top_prescribing_doctor_2_name,
              top_prescribing_doctor_2_patients_per_day: numberOrZero(row.top_prescribing_doctor_2_patients_per_day),
              top_prescribing_doctor_3_name: row.top_prescribing_doctor_3_name,
              top_prescribing_doctor_3_patients_per_day: numberOrZero(row.top_prescribing_doctor_3_patients_per_day)
            }))
          };
        });

        return {
          audit_inner_sheet_id: sheet.auditInnerSheetId,
          psr_code: Number(sheet.psr_code) || 0,
          sheet_name: `CHEMIST ${sheetIndex + 1}`,
          title: REPORT_TITLE,
          month: UsableMonthString || monthCode,
          Division: safeString(sheet.division || header.division),
          Branch: safeString(sheet.branch || header.branch),
          HQ: safeString(selectedPsrHq),
          Area:  "NA",
          psr_id: sheet.psr_id,
          psr_name: sheet.psr_name,
          chemist_name: sheet.chemist_name,
          "ARISTO Brand": brandObject
        };
      })
    };
  };

  const buildSaveDraftPayload = (sourceSheets = sheets) => {
    const reportId = auditMeta.auditReportId || Number(auditreportid) || null;
    const titlePrefix = "CHEMIST AUDIT REPORT";
    const generatedTitle = `${titlePrefix} ${header.division || ""} ${monthCode || ""} ${header.hq || ""}`.trim();
    const selectedPsr = mrList.find(
      (item) => String(item.value) === String(selectedMetaPsrCode)
    );
    const selectedPsrId = safeString(selectedMetaPsrCode);
    const selectedPsrCodeNum = Number(selectedMetaPsrCode) || 0;
    const selectedPsrHq = safeString(selectedPsr?.hq);

    return sourceSheets.map((sheet, sheetIndex) => {
      const brandObject = {};

      sheet.brandGroups.forEach((group) => {
        const brandKey = (group.aristoBrand || "").trim();
        brandObject[brandKey] = {
          potential_per_month_strips: numberOrZero(group.potential_per_month_strips),
          our_sales_per_month_strips: numberOrZero(group.our_sales_per_month_strips),
          data: group.rows.map((row) => ({
            competitor_top_selling_brand: row.competitor_top_selling_brand,
            top_prescribing_doctor_1_name: row.top_prescribing_doctor_1_name,
            top_prescribing_doctor_1_patients_per_day: numberOrZero(
              row.top_prescribing_doctor_1_patients_per_day
            ),
            top_prescribing_doctor_2_name: row.top_prescribing_doctor_2_name,
            top_prescribing_doctor_2_patients_per_day: numberOrZero(
              row.top_prescribing_doctor_2_patients_per_day
            ),
            top_prescribing_doctor_3_name: row.top_prescribing_doctor_3_name,
            top_prescribing_doctor_3_patients_per_day: numberOrZero(
              row.top_prescribing_doctor_3_patients_per_day
            )
          }))
        };
      });

      return {
        audit_report_title: generatedTitle || auditMeta.auditReportTitle || "",
        autid_report_id: reportId,
        audit_report_id: reportId,
        audit_inner_sheet_id: normalizeInnerSheetId(sheet.auditInnerSheetId),
        psr_code: selectedPsrCodeNum,
        audit_report_status: "DRAFT",
        sheets: [
          {
            title: REPORT_TITLE,
            month: UsableMonthString || monthCode,
            sheet_name: `CHEMIST ${sheetIndex + 1}`,
            Division: safeString(sheet.division || header.division),
            Branch: safeString(sheet.branch || header.branch),
            HQ: safeString(selectedPsrHq),
            Area:  "NA",
            psr_id: selectedPsrId || sheet.psr_id,
            psr_name: safeString(selectedPsr?.name || sheet.psr_name),
            chemist_name: sheet.chemist_name,
            "ARISTO Brand": brandObject
          }
        ]
      };
    });
  };

  const downloadJSON = (status) => {
    const payload = buildPayload(status);
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json"
    });

    const fileName = `managers-chemist-audit-${status.toLowerCase()}-${Date.now()}.json`;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadJSON = () => {
    downloadJSON(auditMeta.auditReportStatus || "DRAFT");
  };

  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "Aristo Pharma";
    workbook.created = new Date();
    workbook.title = "Managers Chemist Audit Report";

    const applyCellStyle = (cell) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" }
      };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      cell.font = { size: 10 };
    };

    sheets.forEach((sheet, sheetIndex) => {
      const wsName = sanitizeExcelSheetName(
        sheet.chemist_name,
        `Chemist ${sheetIndex + 1}`
      );
      const ws = workbook.addWorksheet(wsName);
      ws.columns = [
        { width: 24 },
        { width: 24 },
        { width: 24 },
        { width: 12 },
        { width: 24 },
        { width: 12 },
        { width: 24 },
        { width: 12 },
        { width: 14 },
        { width: 14 }
      ];

      ws.mergeCells("A1:J1");
      ws.getCell("A1").value = `${REPORT_TITLE} - ${safeString(sheet.psr_name || commonPsr?.name)}`;
      ws.getCell("A1").font = { bold: true, size: 12 };
      ws.getCell("A1").alignment = { vertical: "middle", horizontal: "left" };

      ws.mergeCells("A2:J2");
      ws.getCell("A2").value = `From: ${abmJSON?.header?.fromName} : ${abmJSON?.header?.designation} : ${abmJSON?.header?.hq ? abmJSON?.header.hq.toUpperCase() : ""} | Division: ${safeString(sheet.division || header.division)} | Branch: ${safeString(sheet.branch || header.branch)} | HQ: ${safeString(commonPsr?.hq)} | Month: ${UsableMonthString || monthCode}`;
      ws.getCell("A2").font = { size: 10 };
      ws.getCell("A2").alignment = { vertical: "middle", horizontal: "left" };

      ws.getCell("A3").value = "Chemist Name";
      ws.getCell("A3").font = { bold: true, size: 10 };
      ws.getCell("B3").value = safeString(sheet.chemist_name);
      ws.getCell("B3").alignment = { vertical: "middle", horizontal: "left" };

      ws.mergeCells("A4:A5");
      ws.mergeCells("B4:B5");
      ws.mergeCells("C4:D4");
      ws.mergeCells("E4:F4");
      ws.mergeCells("G4:H4");
      ws.mergeCells("I4:I5");
      ws.mergeCells("J4:J5");

      ws.getCell("A4").value = "ARISTO Brand";
      ws.getCell("B4").value = "Competitor Top Brand";
      ws.getCell("C4").value = "Top Prescribing Doctor 1";
      ws.getCell("E4").value = "Top Prescribing Doctor 2";
      ws.getCell("G4").value = "Top Prescribing Doctor 3";
      ws.getCell("I4").value = "Potential/Month (Strips)";
      ws.getCell("J4").value = "Our Sales/Month (Strips)";
      ws.getCell("C5").value = "Name";
      ws.getCell("D5").value = "Pts/Day";
      ws.getCell("E5").value = "Name";
      ws.getCell("F5").value = "Pts/Day";
      ws.getCell("G5").value = "Name";
      ws.getCell("H5").value = "Pts/Day";

      for (let r = 4; r <= 5; r += 1) {
        for (let c = 1; c <= 10; c += 1) {
          const cell = ws.getCell(r, c);
          applyCellStyle(cell);
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "D9E2F3" }
          };
          cell.font = { bold: true, size: 10 };
        }
      }

      let rowIndex = 6;
      sheet.brandGroups.forEach((group) => {
        const rowCount = Math.max(group.rows.length, 1);
        const start = rowIndex;
        const end = rowIndex + rowCount - 1;

        if (rowCount > 1) {
          ws.mergeCells(start, 1, end, 1);
          ws.mergeCells(start, 9, end, 9);
          ws.mergeCells(start, 10, end, 10);
        }

        ws.getCell(start, 1).value = group.aristoBrand;
        ws.getCell(start, 9).value = numberOrZero(group.potential_per_month_strips);
        ws.getCell(start, 10).value = numberOrZero(group.our_sales_per_month_strips);

        group.rows.forEach((row, idx) => {
          const current = rowIndex + idx;
          ws.getCell(current, 2).value = row.competitor_top_selling_brand;
          ws.getCell(current, 3).value = row.top_prescribing_doctor_1_name;
          ws.getCell(current, 4).value = numberOrZero(row.top_prescribing_doctor_1_patients_per_day);
          ws.getCell(current, 5).value = row.top_prescribing_doctor_2_name;
          ws.getCell(current, 6).value = numberOrZero(row.top_prescribing_doctor_2_patients_per_day);
          ws.getCell(current, 7).value = row.top_prescribing_doctor_3_name;
          ws.getCell(current, 8).value = numberOrZero(row.top_prescribing_doctor_3_patients_per_day);
        });

        for (let r = start; r <= end; r += 1) {
          for (let c = 1; c <= 10; c += 1) {
            applyCellStyle(ws.getCell(r, c));
          }
        }

        rowIndex = end + 1;
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const fileName = `chemist-audit-${safeString(selectedMetaPsrCode || commonPsr?.value || "psr")}.xlsx`;
    saveAs(blob, fileName);
  };

  const handleSaveDraft = async () => {
    if (!auditreportid) {
      alert("Missing audit report id in URL.");
      return;
    }

    const missingBrand = getMissingBrandSelection();
    if (missingBrand) {
      alert(
        `Please select ARISTO Brand for Sheet ${missingBrand.sheetIndex + 1}, Brand Group ${missingBrand.groupIndex + 1}.`
      );
      return;
    }

    try {
      const sheetsForSave = sheets.map((sheet, sheetIndex) => {
        const currentInnerId = normalizeInnerSheetId(sheet.auditInnerSheetId);
        if (currentInnerId) return sheet;

        const psrCodeForId = Number(selectedMetaPsrCode || sheet.psr_code);
        if (!Number.isFinite(psrCodeForId) || psrCodeForId <= 0) {
          return { ...sheet, auditInnerSheetId: null };
        }

        const generatedInnerId = Number(
          `${Math.trunc(psrCodeForId)}${sheetIndex + 1}`
        );
        return { ...sheet, auditInnerSheetId: generatedInnerId };
      });

      setSheets(sheetsForSave);
      const payloads = buildSaveDraftPayload(sheetsForSave);
      for (const sheetPayload of payloads) {
        await proxy(
          `/api/mis/abm/saveauditsheet/${auditreportid}`,
          sheetPayload,
          "post"
        );
      }
      alert("Draft saved.");
    } catch (error) {
      console.error("Failed to save audit sheets", error);
      alert("Failed to save draft.");
    }
  };

  const handleSaveFinal = async () => {
    if (!auditreportid || !selectedMetaPsrCode) {
      alert("Missing audit report id or PSR code.");
      return;
    }

    try {
      await proxy(
        `/api/mis/abm/report/savefinalchemistaudit/${auditreportid}/${selectedMetaPsrCode}`,
        {},
        "post"
      );
      window.location.reload();
    } catch (error) {
      console.error("Failed to save final", error);
      alert("Failed to save final.");
    }
  };

  return (
    <div className="main-content">
      {showProcessLoader ? (
        <ProcessLoader />
      ) : (
        <div className="row">
          <div className="col-12">

            <div className="card report-card">
              <div className="card-title report-card-title">

                <button
  className="btn btn-outline btn-sm mr-20 fw-500"
  style={{ float: "left" }}
  onClick={() =>
    navigate(
      userType === 4 &&
      safeString(auditMeta?.auditReportStatus).toUpperCase() === "DRAFT"
        ? `/abm-report-01-edit/${auditMeta?.auditDraftId}`
        : `/abm-report-01-view/${auditMeta?.auditDraftId}` // change id if view uses another field
    )
  }
>
  BACK
</button>

                CHEMIST AUDIT REPORT &nbsp;|&nbsp;

            From: {abmJSON?.header?.fromName} : {abmJSON?.header?.designation} : {abmJSON?.header?.hq ? abmJSON?.header.hq.toUpperCase() : ""} &nbsp;|&nbsp;  Division: {header.division} &nbsp;|&nbsp; Branch:  {header.branch} &nbsp;|&nbsp; Status: {auditMeta.auditReportStatus || "-"} &nbsp;|&nbsp; {MONTHS[parseInt(auditMeta.monthCode?.toString().slice(4))-1]} {auditMeta.monthCode?.toString().slice(0,4)}




              </div>

              <div className="card-body">

               
                <div className="row g-2 mb-3">
                  
                  
                   <div className="col-md-6 ">
                        
                        <div className="report-option-row d-flex">
                <div className="report-option-item">

                        <DropDownControl
                          key={`psr_code_common_${commonPsrCode}`}
                          id="psr_code"
                          name="psr_code"
                          data={psrCodeOptions}
                          selectedValue={commonPsrCode}
                          onChange={(selectedPsrCode) => {
                            handlePsrCodeSelect(selectedPsrCode);
                          }}
                        />
                        </div>
                 
                     <div className="report-option-item">
                        <label className="ml-2 fw-800">HQ </label>
                        <input
                          type="text"
                          className="form-control ml-2 hq_name_top"
                          value={safeString(commonPsr?.hq)}
                          readOnly
                        />
                        </div>
                          </div>
                      </div>
                </div>

                <div className="d-flex align-items-center mb-3" style={{ gap: "8px", overflowX: "auto" }}>
                  {sheets.map((sheet, index) => {
                    const isActive = activeSheetId === sheet.id;
                    return (
                      <button
                        key={sheet.id}
                        type="button"
                        className={`btn ${isActive ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setActiveSheetId(sheet.id)}
                      >
                        {`Chemist Sheet #${index + 1}`}
                        {sheets.length > 1 && (
                          <span
                            role="button"
                            aria-label={`Remove Chemist ${index + 1}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeSheet(sheet.id);
                            }}
                            style={{ marginLeft: "8px", fontWeight: 700 }}
                          >
                            x
                          </span>
                        )}
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={sheets.length >= 10}
                    onClick={addSheet}
                  >
                    + Add Chemist Sheet
                  </button>
                </div>

                {activeSheet && (
                  <>
                    <div className="row g-2 mb-3">
                      <div className="col-md-3">
                      <label className="form-label">Chemist Name</label>
                      <input
                        type="text"
                        className="form-control hq_name_top"
                        value={activeSheet.chemist_name}
                        onChange={(e) =>
                          updateSheet(activeSheet.id, { chemist_name: e.target.value })
                        }
                      />
                    </div>

                      
                     
                     
                    </div>

                    

                    <div className="table-responsive">
                      <table className="table table-bordered align-middle chemist-audit-table">
                      
                        <thead><tr class="brand-table-header-group">
                          <th class="th-brand med-name" rowspan="2">ARISTO Brand</th>
                          <th class="th-competitor med-name" rowspan="2">Competitor<br/>Top Brand</th>
                          <th class="th-doctor-group docnamegroup" colspan="2">Top Prescribing Doctor 1</th>
                          <th class="th-doctor-group docnamegroup" colspan="2">Top Prescribing Doctor 2</th>
                          <th class="th-doctor-group docnamegroup last" colspan="2">Top Prescribing Doctor 3</th>
                          <th class="th-numeric" rowspan="2">Potential<br/>/Month<br/>(strips)</th>
                          <th class="th-numeric" rowspan="2">Our Sales<br/>/Month<br/>(strips)</th>
                          <th class="th-action med-name" rowspan="2"></th>
                          </tr><tr class="brand-table-header-sub">
                            <th class="th-sub docnamesub">Name</th>
                            <th class="th-sub docnamesub th-sub-pts">Pts/Day</th>
                            <th class="th-sub docnamesub ">Name</th>
                            <th class="th-sub docnamesub th-sub-pts">Pts/Day</th>
                            <th class="th-sub docnamesub">Name</th>
                            <th class="th-sub docnamesub th-sub-pts">Pts/Day</th>
                            </tr></thead>
                          <tbody>
                            {activeSheet.brandGroups.map((group, groupIndex) =>
                              group.rows.map((row, rowIndex) => (
                                <tr key={`${groupIndex}-${rowIndex}`}>
                                  {rowIndex === 0 && (
                                    <td rowSpan={group.rows.length} className="brandSelect">
                                      <DropDownControl
                                        key={`brand_dd_${activeSheet.id}_${groupIndex}_${group.aristoBrand}`}
                                        id={`brand_${activeSheet.id}_${groupIndex}`}
                                        name={`brand_${activeSheet.id}_${groupIndex}`}
                                        data={brandOptions}
                                        selectedValue={group.aristoBrand}
                                        onChange={(selectedValue) =>
                                          updateBrandGroup(
                                            activeSheet.id,
                                            groupIndex,
                                            "aristoBrand",
                                            selectedValue
                                          )
                                        }
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-xs btn-outline btn-danger mt-2"
                                        onClick={() => removeBrandGroup(activeSheet.id, groupIndex)}
                                      >
                                        Remove Brand Group
                                      </button>
                                        <button
                                        type="button"
                                        className="btn btn-xs btn-primary btn-primary mt-2 ml-2"
                                        onClick={() => addCompetitorRow(activeSheet.id, groupIndex)}
                                      >
                                        Add Competitor
                                      </button>
                                    </td>
                                  )}

                                  <td>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={row.competitor_top_selling_brand}
                                      onChange={(e) =>
                                        updateCompetitorRow(
                                          activeSheet.id,
                                          groupIndex,
                                          rowIndex,
                                          "competitor_top_selling_brand",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      maxLength={250}
                                      className="form-control"
                                      value={row.top_prescribing_doctor_1_name}
                                      onChange={(e) =>
                                        updateCompetitorRow(
                                          activeSheet.id,
                                          groupIndex,
                                          rowIndex,
                                          "top_prescribing_doctor_1_name",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control dr-pts"
                                      value={row.top_prescribing_doctor_1_patients_per_day}
                                      onChange={(e) =>
                                        updateCompetitorRow(
                                          activeSheet.id,
                                          groupIndex,
                                          rowIndex,
                                          "top_prescribing_doctor_1_patients_per_day",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={row.top_prescribing_doctor_2_name}
                                      onChange={(e) =>
                                        updateCompetitorRow(
                                          activeSheet.id,
                                          groupIndex,
                                          rowIndex,
                                          "top_prescribing_doctor_2_name",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control dr-pts"
                                      value={row.top_prescribing_doctor_2_patients_per_day}
                                      onChange={(e) =>
                                        updateCompetitorRow(
                                          activeSheet.id,
                                          groupIndex,
                                          rowIndex,
                                          "top_prescribing_doctor_2_patients_per_day",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={row.top_prescribing_doctor_3_name}
                                      onChange={(e) =>
                                        updateCompetitorRow(
                                          activeSheet.id,
                                          groupIndex,
                                          rowIndex,
                                          "top_prescribing_doctor_3_name",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control dr-pts"
                                      value={row.top_prescribing_doctor_3_patients_per_day}
                                      onChange={(e) =>
                                        updateCompetitorRow(
                                          activeSheet.id,
                                          groupIndex,
                                          rowIndex,
                                          "top_prescribing_doctor_3_patients_per_day",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </td>

                                  {rowIndex === 0 && (
                                    <td rowSpan={group.rows.length}>
                                      <input
                                        type="number"
                                        className="form-control dr-pts"
                                        value={group.potential_per_month_strips}
                                        onChange={(e) =>
                                          updateBrandGroup(
                                            activeSheet.id,
                                            groupIndex,
                                            "potential_per_month_strips",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </td>
                                  )}

                                  {rowIndex === 0 && (
                                    <td rowSpan={group.rows.length}>
                                      <input
                                        type="number"
                                        className="form-control dr-pts"
                                        value={group.our_sales_per_month_strips}
                                        onChange={(e) =>
                                          updateBrandGroup(
                                            activeSheet.id,
                                            groupIndex,
                                            "our_sales_per_month_strips",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </td>
                                  )}

                                  <td>
                                    <div className="d-flex" style={{ gap: "6px" }}>
                                    
                                      <button
                                        type="button"
                                        className="btn btn-xs btn-outline btn-danger"
                                        onClick={() =>
                                          removeCompetitorRow(activeSheet.id, groupIndex, rowIndex)
                                        }
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                        </div>

                          <button
                            type="button"
                            className="btn btn-outline-secondary mb-3"
                            onClick={() => addBrandGroup(activeSheet.id)}
                          >
                            Add Brand Group
                          </button>
                        </>
            )}

                          <div className="d-flex" style={{ gap: "10px" }}>
                         
                            <button type="button" className="btn btn-primary" onClick={handleDownloadExcel}>
                              Download Excel
                            </button>
                            {safeString(auditMeta.auditReportStatus).toUpperCase() === "DRAFT" &&  (userType == 4) ? (
                              <button type="button" className="btn btn-primary" onClick={handleSaveDraft}>
                                Save Draft
                              </button>
                            )
                          :""}
                            {/* {safeString(auditMeta.auditReportStatus).toUpperCase() === "DRAFT" &&  (userType == 4) ? (
                              <button type="button" className="btn btn-primary" onClick={handleSaveFinal}>
                                Save Final
                              </button>
                            ) : ""} */}
                         
                          </div>
                        </div>
                    </div>
                  </div>
              </div>
      )}
            </div>
            );
};

            export default ChemistAuditEdit;
